// src/App.js
import React from "react"
import axios from "axios"
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom"
import { ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import GenerateKeyScreen from "./screens/GenerateKeyScreen"
// import BalanceScreen from "./screens/BalanceScreen"
import DashboardScreen from "./screens/DashboardScreen"
import TransactionsScreen from "./screens/TransactionsScreen"
import CommunityReportScreen from "./screens/CommunityReportScreen"
import ProfileScreen from "./screens/ProfileScreen"
import FaqScreen from "./screens/FaqScreen"
import AdminLoginScreen from "./screens/AdminLoginScreen"
import AdminDashboardScreen from "./screens/AdminDashboardScreen"
import ManageAccountsScreen from "./screens/ManageAccountsScreen"
import AdminTransactionsScreen from "./screens/AdminTransactionsScreen"
import AdminReportsScreen from "./screens/AdminReportsScreen"
import AdminBitcoinsScreen from "./screens/AdminBitcoinsScreen"
import AdminExchangeRatesScreen from "./screens/AdminExchangeRatesScreen"
import "./styles.css"
import {
  cacheProfileToLocal,
  clearCachedProfile,
  PROFILE_CACHE_EVENT,
  readCachedProfile,
} from "./utils/profileCache"

const fetchAndCacheProfileFromApi = async (baseUrl, token) => {
  if (!baseUrl || !token) return null
  try {
    const response = await axios.get(`${baseUrl}/profile`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (response?.data) {
      cacheProfileToLocal(response.data)
      return response.data
    }
  } catch (error) {
    console.error("Failed to cache profile:", error)
  }
  return null
}

// AuthRoute wrapper
const AuthRoute = ({ children }) => {
  const hasKey = Boolean(localStorage.getItem("auth_key"))
  return hasKey ? children : <Navigate to="/" replace />
}

// AdminRoute wrapper
const AdminRoute = ({ children }) => {
  const isAdmin = Boolean(localStorage.getItem("is_admin"))
  return isAdmin ? children : <Navigate to="/admin" replace />
}

// Redirect to admin dashboard if admin authenticated
const RedirectIfAdminAuthenticated = ({ children }) => {
  const isAdmin = Boolean(localStorage.getItem("is_admin"))
  return isAdmin ? <Navigate to="/admin/dashboard" replace /> : children
}

// Redirect to dashboard if authenticated
const RedirectIfAuthenticated = ({ children }) => {
  const hasKey = Boolean(localStorage.getItem("auth_key"))
  return hasKey ? <Navigate to="/dashboard" replace /> : children
}

// Auth key setter route
const AuthKeySetter = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  React.useEffect(() => {
    const key = searchParams.get("key")
    const baseUrl = process.env.REACT_APP_BASE_URL

    if (key) {
      localStorage.setItem("auth_key", key)
      fetchAndCacheProfileFromApi(baseUrl, key).finally(() => {
        navigate("/dashboard", { replace: true })
      })
    }
  }, [searchParams, navigate])

  return null
}

// App shell with a glass sidebar (desktop) / slide-in drawer (mobile)
const AppShell = ({ children }) => {
  const [drawerOpen, setDrawerOpen] = React.useState(false)
  const [, profileRevision] = React.useReducer((n) => n + 1, 0)
  const navigate = useNavigate()
  const location = useLocation()

  const handleLogout = () => {
    localStorage.removeItem("auth_key")
    localStorage.removeItem("is_admin")
    clearCachedProfile()
    navigate("/", { replace: true })
  }

  const closeDrawer = () => setDrawerOpen(false)

  React.useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "unset"
    return () => {
      document.body.style.overflow = "unset"
    }
  }, [drawerOpen])

  React.useEffect(() => {
    const refreshProfile = () => profileRevision()
    window.addEventListener(PROFILE_CACHE_EVENT, refreshProfile)
    return () => window.removeEventListener(PROFILE_CACHE_EVENT, refreshProfile)
  }, [])

  React.useEffect(() => {
    const token = localStorage.getItem("auth_key")
    const baseUrl = process.env.REACT_APP_BASE_URL
    if (!token || !baseUrl) return
    fetchAndCacheProfileFromApi(baseUrl, token)
  }, [location.pathname])

  let active = ""
  if (location.pathname === "/dashboard") {
    active = location.hash === "#transactions" ? "tx" : "dash"
  } else if (location.pathname === "/transactions") {
    active = "tx"
  } else if (location.pathname === "/community-report") {
    active = "report"
  } else if (location.pathname === "/profile") {
    active = "profile"
  } else if (location.pathname === "/faq") {
    active = "faq"
  }

  const { displayName, email, initials } = readCachedProfile()

  const navItems = [
    { id: "dash", label: "Dashboard", href: "/dashboard" },
    { id: "tx", label: "Transactions", href: "/dashboard#transactions" },
    { id: "report", label: "Community Report", href: "/community-report" },
    { id: "profile", label: "Profile", href: "/profile" },
    { id: "faq", label: "Help & FAQ", href: "/faq" },
  ]

  return (
    <div className="app-shell">
      <header className="app-topbar">
        <img src="/logo.svg" alt="Project 31 Logo" className="topbar-logo" />
        <button
          className="hamburger"
          onClick={() => setDrawerOpen(true)}
          aria-label="Open menu"
        >
          Menu
        </button>
      </header>

      <aside className={`app-sidebar ${drawerOpen ? "open" : ""}`}>
        <div className="sidebar-top">
          <img src="/logo.svg" alt="Project 31 Logo" className="sidebar-logo" />
          <button
            className="sidebar-close"
            onClick={closeDrawer}
            aria-label="Close menu"
          >
            ×
          </button>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <a
              key={item.id}
              href={item.href}
              className={`sidebar-link ${active === item.id ? "active" : ""}`}
              onClick={closeDrawer}
            >
              <span className="sidebar-link-dot" aria-hidden="true" />
              <span>{item.label}</span>
            </a>
          ))}
        </nav>

        <div className="sidebar-spacer" />

        <div className="sidebar-user">
          <div className="sidebar-avatar">{initials}</div>
          <div className="sidebar-user-meta">
            <div className="sidebar-user-name">{displayName || email}</div>
            {email && displayName !== email && (
              <div className="sidebar-user-email">{email}</div>
            )}
          </div>
        </div>

        <button
          className="sidebar-logout"
          onClick={() => {
            handleLogout()
            closeDrawer()
          }}
        >
          Logout
        </button>
      </aside>

      {drawerOpen && (
        <div className="app-drawer-overlay" onClick={closeDrawer} />
      )}

      <main className="app-main">{children}</main>
    </div>
  )
}

function App() {
  return (
    <Router>
      <ToastContainer />
      <Routes>
        <Route
          path="/"
          element={
            <RedirectIfAuthenticated>
              <GenerateKeyScreen />
            </RedirectIfAuthenticated>
          }
        />
        <Route path="/auth" element={<AuthKeySetter />} />
        <Route
          path="/dashboard"
          element={
            <AuthRoute>
              <AppShell>
                <DashboardScreen />
              </AppShell>
            </AuthRoute>
          }
        />
        <Route
          path="/transactions"
          element={
            <AuthRoute>
              <AppShell>
                <TransactionsScreen />
              </AppShell>
            </AuthRoute>
          }
        />
        <Route
          path="/community-report"
          element={
            <AuthRoute>
              <AppShell>
                <CommunityReportScreen />
              </AppShell>
            </AuthRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <AuthRoute>
              <AppShell>
                <ProfileScreen />
              </AppShell>
            </AuthRoute>
          }
        />
        <Route
          path="/faq"
          element={
            <AuthRoute>
              <AppShell>
                <FaqScreen />
              </AppShell>
            </AuthRoute>
          }
        />
        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <RedirectIfAdminAuthenticated>
              <AdminLoginScreen />
            </RedirectIfAdminAuthenticated>
          }
        />
        <Route
          path="/admin/dashboard"
          element={
            <AdminRoute>
              <AdminDashboardScreen />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/manage-accounts"
          element={
            <AdminRoute>
              <ManageAccountsScreen />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/reports"
          element={
            <AdminRoute>
              <AdminReportsScreen />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/transactions"
          element={
            <AdminRoute>
              <AdminTransactionsScreen />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/add-transaction"
          element={
            <AdminRoute>
              <AdminTransactionsScreen />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/exchange-rates"
          element={
            <AdminRoute>
              <AdminExchangeRatesScreen />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/bitcoins"
          element={
            <AdminRoute>
              <AdminBitcoinsScreen />
            </AdminRoute>
          }
        />
      </Routes>
    </Router>
  )
}

export default App
